import React from "react";
import { motion } from "framer-motion";
import { COLLEGE } from "../data/college";
import { CircuitTrace } from "./Decorations";
import { Target, Eye } from "lucide-react";
import { useT } from "../i18n/LanguageProvider";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const About = () => {
  const { t } = useT();
  return (
    <section
      id="about"
      className="relative py-16 md:py-24 lg:py-32 overflow-hidden"
      data-testid="about-section"
    >
      <CircuitTrace className="absolute right-0 top-0 w-[400px] opacity-20" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
        >
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-2 bg-gradient-to-br from-[#00E5FF]/30 via-transparent to-[#39FF14]/20 rounded-3xl blur-2xl" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[#00E5FF]/25 group">
              <img
                src={COLLEGE.about_image}
                alt="Mar Elias campus"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#04060a] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex justify-between items-end">
                <div>
                  <div className="kicker mb-1">EST · {COLLEGE.est}</div>
                  <div className="font-heading text-white text-lg">
                    {t("about.campusBadge")}
                  </div>
                </div>
                <div className="font-mono text-xs text-[#39FF14]">● LIVE</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-7">
            <div className="kicker">{t("about.kicker")}</div>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              {t("about.headlineA")}{" "}
              <span className="text-[#00E5FF] text-glow-cyan">
                {t("about.headlineB")}
              </span>
              , {t("about.headlineC")}
            </h2>
            <p className="font-body text-base md:text-lg text-[#94a3b8] leading-relaxed max-w-2xl">
              {t("about.body")}
            </p>

            <div className="grid sm:grid-cols-2 gap-4 pt-3">
              <div className="glass p-6 brackets">
                <Eye className="w-6 h-6 text-[#00E5FF] mb-3" />
                <div className="font-heading text-white text-lg mb-2">
                  {t("about.vision")}
                </div>
                <p className="text-sm text-[#94a3b8] leading-relaxed">
                  {t("about.visionBody")}
                </p>
              </div>
              <div className="glass p-6 brackets">
                <Target className="w-6 h-6 text-[#39FF14] mb-3" />
                <div className="font-heading text-white text-lg mb-2">
                  {t("about.mission")}
                </div>
                <p className="text-sm text-[#94a3b8] leading-relaxed">
                  {t("about.missionBody")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3">
              {[
                { v: "19+", l: t("about.years") },
                { v: "5", l: t("about.ug") },
                { v: "3", l: t("about.pg") },
              ].map((s) => (
                <div
                  key={s.l}
                  className="border border-[#00E5FF]/15 bg-white/[0.02] rounded-md p-4 text-center"
                >
                  <div className="font-heading text-3xl md:text-4xl text-[#00E5FF] text-glow-cyan">
                    {s.v}
                  </div>
                  <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.15em] text-white/55 mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
